import React, { useRef, useEffect, useCallback } from 'react';

/**
 * AnimatedGlitch - A TV static effect component with configurable controls
 * Creates an animated pixel grid that simulates TV static/glitch effect
 *
 * @param {number} width - Canvas width in pixels (default: 195)
 * @param {number} height - Canvas height in pixels (default: 195)
 * @param {number} cellSize - Size of each pixel cell (default: 15)
 * @param {number} speed - Base animation speed in ms between frames (default: 100)
 * @param {number} speedVariance - How much speed can vary 0-1 (default: 0.5)
 * @param {number} density - Probability 0-1 of a cell being filled (default: 0.4)
 * @param {number} persistence - Probability 0-1 that a pixel persists between frames (default: 0.6)
 * @param {string} primaryColor - Main glitch color (default: '#FB923C' - orange)
 * @param {string} secondaryColor - Secondary color (default: '#03111F' - dark blue)
 * @param {string} tertiaryColor - Third color for depth (default: '#FCD34D' - lighter orange/yellow)
 * @param {string} quaternaryColor - Fourth color for depth (default: '#051525' - darker blue)
 * @param {string} className - Additional CSS classes
 */
const AnimatedGlitch = ({
  width = 195,
  height = 195,
  cellSize = 15,
  speed = 100,
  speedVariance = 0.5,
  density = 0.4,
  persistence = 0.6,
  primaryColor = '#FB923C',
  secondaryColor = '#03111F',
  tertiaryColor = '#FCD34D',
  quaternaryColor = '#051525',
  className = '',
}) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const lastFrameTime = useRef(0);
  const nextFrameDelay = useRef(speed);
  const pixelStateRef = useRef(null);

  // Calculate random delay with variance - creates rapid bursts and prolonged pauses
  const getRandomDelay = useCallback(() => {
    // Use exponential distribution for more natural glitch timing
    // This creates occasional rapid bursts and longer pauses
    const rand = Math.random();

    // 20% chance of rapid burst (very short delay)
    if (rand < 0.2) {
      return speed * (0.1 + Math.random() * 0.3); // 10-40% of base speed
    }
    // 15% chance of prolonged pause (long delay)
    if (rand < 0.35) {
      return speed * (1.5 + Math.random() * speedVariance * 3); // 150-300%+ of base speed
    }
    // 65% normal variation around base speed
    const variance = (Math.random() - 0.5) * 2 * speedVariance;
    return speed * (1 + variance);
  }, [speed, speedVariance]);

  // Initialize or get pixel state grid
  const getPixelState = useCallback((cols, rows) => {
    if (!pixelStateRef.current ||
        pixelStateRef.current.length !== rows ||
        pixelStateRef.current[0]?.length !== cols) {
      // Initialize grid with null (empty) values
      pixelStateRef.current = Array(rows).fill(null).map(() =>
        Array(cols).fill(null).map(() => ({
          active: false,
          color: null,
          opacity: 0,
        }))
      );
    }
    return pixelStateRef.current;
  }, []);

  // Get a random color from the four available colors
  const getRandomColor = useCallback(() => {
    // Weight distribution: primary 35%, secondary 35%, tertiary 15%, quaternary 15%
    const rand = Math.random();
    if (rand < 0.35) return primaryColor;
    if (rand < 0.70) return secondaryColor;
    if (rand < 0.85) return tertiaryColor;
    return quaternaryColor;
  }, [primaryColor, secondaryColor, tertiaryColor, quaternaryColor]);

  // Generate random pixel grid pattern with persistence
  const generatePattern = useCallback((ctx, canvasWidth, canvasHeight) => {
    const cols = Math.ceil(canvasWidth / cellSize);
    const rows = Math.ceil(canvasHeight / cellSize);
    const pixelState = getPixelState(cols, rows);

    // Clear canvas
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);

    // Update and draw pixels
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const pixel = pixelState[row][col];

        // Create diagonal density gradient (more pixels in upper-right)
        const diagonalFactor = (col + (rows - row)) / (cols + rows);
        const adjustedDensity = density * diagonalFactor * 1.5;

        // Decide if this pixel should update or persist
        const shouldPersist = pixel.active && Math.random() < persistence;

        if (shouldPersist) {
          // Keep existing pixel - maybe slightly vary opacity for shimmer effect
          const shimmer = 0.9 + Math.random() * 0.1;
          ctx.globalAlpha = pixel.opacity * shimmer;
          ctx.fillStyle = pixel.color;
          ctx.fillRect(col * cellSize, row * cellSize, cellSize, cellSize);
        } else {
          // Generate new pixel state
          if (Math.random() < adjustedDensity) {
            // Random opacity between 0.7 and 1
            const opacity = 0.7 + Math.random() * 0.3;
            const color = getRandomColor();

            // Store state for persistence
            pixel.active = true;
            pixel.color = color;
            pixel.opacity = opacity;

            ctx.globalAlpha = opacity;
            ctx.fillStyle = color;
            ctx.fillRect(col * cellSize, row * cellSize, cellSize, cellSize);
          } else {
            // Clear this pixel
            pixel.active = false;
            pixel.color = null;
            pixel.opacity = 0;
          }
        }
      }
    }
  }, [cellSize, density, persistence, getPixelState, getRandomColor]);

  // Animation loop with variable timing
  const animate = useCallback((timestamp) => {
    if (!canvasRef.current) return;

    // Check if enough time has passed based on variable delay
    if (timestamp - lastFrameTime.current >= nextFrameDelay.current) {
      const ctx = canvasRef.current.getContext('2d');
      generatePattern(ctx, width, height);
      lastFrameTime.current = timestamp;
      // Set new random delay for next frame
      nextFrameDelay.current = getRandomDelay();
    }

    animationRef.current = requestAnimationFrame(animate);
  }, [generatePattern, width, height, getRandomDelay]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');

    // Initial render
    generatePattern(ctx, width, height);
    nextFrameDelay.current = getRandomDelay();

    // Start animation
    animationRef.current = requestAnimationFrame(animate);

    // Cleanup
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [animate, generatePattern, width, height, getRandomDelay]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className={className}
      aria-hidden="true"
    />
  );
};

export default AnimatedGlitch;
